import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getStations } from '../services/api';
import { parseSectors } from '../lib/sectors';

/**
 * Loads the station list once and exposes each station's safe sectors by id.
 * Until it resolves (or if it fails) `byId` is empty and consumers fall back
 * to the hardcoded site constants via `suitableDirections`.
 */
const StationSectorsContext = createContext({ byId: {}, loaded: false });

export const StationSectorsProvider = ({ children }) => {
    const [byId, setById] = useState({});
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        let alive = true;
        getStations()
            .then((rows) => {
                if (!alive) return;
                const map = {};
                rows.forEach((r) => { const s = parseSectors(r); if (s) map[String(r.ST_ID)] = s; });
                setById(map);
            })
            .catch(() => { /* fall back to constants */ })
            .finally(() => { if (alive) setLoaded(true); });
        return () => { alive = false; };
    }, []);
    const value = useMemo(() => ({ byId, loaded }), [byId, loaded]);
    return <StationSectorsContext.Provider value={value}>{children}</StationSectorsContext.Provider>;
};

export const useStationSectors = () => useContext(StationSectorsContext);

/** Sectors for one site (by numeric id), or null. */
export const useSiteSectors = (site) => {
    const { byId } = useStationSectors();
    return site ? byId[String(site.id)] || null : null;
};
